import { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import Layout from "@/components/Layout";
import Login from "@/pages/login/Login";
import Users from "@/pages/users/Users";
import UsersLayout from "@/pages/users/UsersLayout";
import AccessProfiles from '@/pages/users/AccessProfiles';
import { Merchants, MerchantBalances } from "@/pages/merchants";
import TradersList from "@/pages/traders/components/TradersList";
import TraderRates from "@/pages/traders/components/TraderRates";
import TraderBalances from "@/pages/traders/components/TraderBalances";
import TraderRegistry from "@/pages/traders/components/TraderRegistry";
import TraderSupport from "@/pages/traders/components/TraderSupport";
import TraderTransactionsSummary from "@/pages/traders/components/TraderTransactionsSummary";
import TraderRegistrations from "@/pages/traders/components/TraderRegistrations";
import TraderGroups from "@/pages/traders/components/TraderGroups";
import TraderDevices from "@/pages/traders/components/TraderDevices";
import Finance from "@/pages/finance/Finance";
import GeoEmergency from "@/pages/geoEmergency/GeoEmergency";
import SupportShifts from "@/pages/supportShifts/SupportShifts";
import {
  PayoutsOutLayout,
  PayoutsOutLimits,
  PayoutsOutConfirmation,
  PayoutsOutCancelled,
  PayoutsOutStatistics,
} from "@/pages/payouts-out";
import NotFound from "@/pages/notFound/NotFound";
import Transactions from "@/pages/transactions/Transactions";
import Appeals from "@/pages/appeals/Appeals";
import Requisites from "@/pages/requisites/Requisites";
import Settings from "@/pages/settings/Settings";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { checkAuth } from "@/store/slices/authSlice";
import Cookies from "js-cookie";
import TradersLayout from "@/pages/traders/components/TradersLayout";
import { canAccessPage, getDefaultPathForUser, PageKey } from "@/auth/accessMatrix";
import Deposits from "@/pages/deposits/Deposits";
import SandboxRequisites from "@/pages/merchants/components/SandboxRequisites";
import InviteTokens from "@/pages/users/components/InviteTokens";
import Settlements from "@/pages/settlements/Settlements";
import Dashboard from "@/pages/dashboard/Dashboard";
import Antifraud from "@/pages/antifraud/Antifraud";
import Verification from "@/pages/verification/Verification";
import NewsBroadcast from "@/pages/news/NewsBroadcast";
import Offers from "@/pages/offers/Offers";
import TraderDirectory from "@/pages/development/TraderDirectory";
import RefundRequests from "@/pages/refunds/RefundRequests";
import Penalties from "@/pages/penalties/Penalties";
import Losses from "@/pages/losses/Losses";

const PayoutsOutIndexRedirect = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const target = canAccessPage(user, PageKey.PAYOUTS_OUT_OPERATIONS)
    ? "/payouts-out/invoices"
    : "/payouts-out/statistics";

  return <Navigate to={target} replace />;
};

const App = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, loading, user } = useSelector(
    (state: RootState) => state.auth
  );
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  useEffect(() => {
    const checkAuthStatus = async () => {
      const accessToken = Cookies.get('access_token');

      if (accessToken) {
        try {
          await dispatch(checkAuth() as any);
        } catch (error) {
          console.error("Auth check failed:", error);
          Cookies.remove('access_token');
        }
      }
      setIsAuthChecked(true);
    };

    checkAuthStatus();
  }, [dispatch]);

  useEffect(() => {
    window.tradersLayoutRef = null;
    window.tradersListRef = null;
    window.traderRatesRef = null;
    window.traderBalancesRef = null;
    window.traderRegistryRef = null;
    window.traderSupportRef = null;
    window.traderGroupsRef = null;
  }, []);

  if (!isAuthChecked || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to={getDefaultPathForUser(user)} replace />} />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute page={PageKey.DASHBOARD}>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="users"
          element={
            <ProtectedRoute page={PageKey.USERS}>
              <UsersLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Users />} />
          <Route path="devices" element={<TraderDevices />} />
          <Route path="access-profiles" element={<AccessProfiles />} />
        </Route>
        <Route
          path="merchants"
          element={
            <ProtectedRoute page={PageKey.MERCHANTS}>
              <Merchants />
            </ProtectedRoute>
          }
        />
        <Route
          path="merchant-balances"
          element={
            <ProtectedRoute page={PageKey.MERCHANT_BALANCES}>
              <MerchantBalances />
            </ProtectedRoute>
          }
        />
        <Route
          path="traders"
          element={
            <ProtectedRoute>
              <TradersLayout ref={(ref) => { window.tradersLayoutRef = ref; }} />
            </ProtectedRoute>
          }
        >
          <Route index element={<ProtectedRoute page={PageKey.TRADERS}><TradersList ref={(ref) => { window.tradersListRef = ref; }} /></ProtectedRoute>} />
          <Route path="rates" element={<ProtectedRoute page={PageKey.TRADER_OPERATIONS}><TraderRates ref={(ref) => { window.traderRatesRef = ref; }} /></ProtectedRoute>} />
          <Route path="balances" element={<ProtectedRoute page={PageKey.TRADER_OPERATIONS}><TraderBalances ref={(ref) => { window.traderBalancesRef = ref; }} /></ProtectedRoute>} />
          <Route path="registry" element={<ProtectedRoute page={PageKey.TRADER_OPERATIONS}><TraderRegistry ref={(ref) => { window.traderRegistryRef = ref; }} /></ProtectedRoute>} />
          <Route path="support" element={<ProtectedRoute page={PageKey.TRADER_SUPPORT}><TraderSupport ref={(ref) => { window.traderSupportRef = ref; }} /></ProtectedRoute>} />
          <Route path="statistics" element={<ProtectedRoute page={PageKey.TRADER_OPERATIONS}><TraderTransactionsSummary /></ProtectedRoute>} />
          <Route path="registrations" element={<ProtectedRoute page={PageKey.TRADER_OPERATIONS}><TraderRegistrations /></ProtectedRoute>} />
          <Route path="groups" element={<ProtectedRoute page={PageKey.TRADER_OPERATIONS}><TraderGroups ref={(ref) => { window.traderGroupsRef = ref; }} /></ProtectedRoute>} />
        </Route>
        <Route
          path="geo-emergency"
          element={
            <ProtectedRoute page={PageKey.GEO_EMERGENCY}>
              <GeoEmergency />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance"
          element={
            <ProtectedRoute page={PageKey.FINANCE}>
              <Finance />
            </ProtectedRoute>
          }
        />
        <Route path="offers" element={<ProtectedRoute page={PageKey.OFFERS}><Offers /></ProtectedRoute>} />
        <Route
          path="payouts-out"
          element={
            <ProtectedRoute page={PageKey.PAYOUTS_OUT}>
              <PayoutsOutLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<PayoutsOutIndexRedirect />} />
          <Route path="statistics" element={<PayoutsOutStatistics />} />
          <Route
            path="invoices"
            element={
              <ProtectedRoute page={PageKey.PAYOUTS_OUT_OPERATIONS}>
                <PayoutsOutLimits />
              </ProtectedRoute>
            }
          />
          <Route
            path="confirmation"
            element={
              <ProtectedRoute page={PageKey.PAYOUTS_OUT_OPERATIONS}>
                <PayoutsOutConfirmation />
              </ProtectedRoute>
            }
          />
          <Route
            path="cancelled"
            element={
              <ProtectedRoute page={PageKey.PAYOUTS_OUT_OPERATIONS}>
                <PayoutsOutCancelled />
              </ProtectedRoute>
            }
          />
          <Route path="limits" element={<PayoutsOutIndexRedirect />} />
        </Route>
        <Route
          path="transactions"
          element={
            <ProtectedRoute page={PageKey.TRANSACTIONS}>
              <Transactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="appeals"
          element={
            <ProtectedRoute page={PageKey.APPEALS}>
              <Appeals />
            </ProtectedRoute>
          }
        />
        <Route
          path="requisites"
          element={
            <ProtectedRoute page={PageKey.REQUISITES}>
              <Requisites />
            </ProtectedRoute>
          }
        />
        <Route
          path="sandbox-requisites"
          element={
            <ProtectedRoute page={PageKey.SANDBOX_REQUISITES}>
              <SandboxRequisites />
            </ProtectedRoute>
          }
        />
        <Route
          path="invite-tokens"
          element={
            <ProtectedRoute page={PageKey.INVITE_TOKENS}>
              <InviteTokens />
            </ProtectedRoute>
          }
        />
        <Route
          path="settlements"
          element={
            <ProtectedRoute page={PageKey.SETTLEMENTS}>
              <Settlements />
            </ProtectedRoute>
          }
        />
        <Route
          path="antifraud"
          element={<ProtectedRoute page={PageKey.ANTIFRAUD}><Antifraud /></ProtectedRoute>}
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute page={PageKey.SETTINGS}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route path="deposits" element={<ProtectedRoute page={PageKey.DEPOSITS}><Deposits /></ProtectedRoute>} />
        <Route path="verification" element={<ProtectedRoute page={PageKey.VERIFICATION}><Verification /></ProtectedRoute>} />
        <Route path="news-broadcast" element={<ProtectedRoute page={PageKey.NEWS}><NewsBroadcast /></ProtectedRoute>} />
        <Route path="development/traders" element={<ProtectedRoute page={PageKey.DEVELOPMENT}><TraderDirectory /></ProtectedRoute>} />
        <Route path="refund-requests" element={<ProtectedRoute page={PageKey.REFUND_REQUESTS}><RefundRequests /></ProtectedRoute>} />
        <Route path="penalties" element={<ProtectedRoute page={PageKey.PENALTIES}><Penalties /></ProtectedRoute>} />
        <Route path="losses" element={<ProtectedRoute page={PageKey.LOSSES}><Losses /></ProtectedRoute>} />
        <Route
          path="support-shifts"
          element={
            <ProtectedRoute page={PageKey.SUPPORT_SHIFTS}>
              <SupportShifts />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
